import {
	ArrowRightIcon,
	ChatBubbleLeftRightIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	Cog6ToothIcon,
	LinkIcon,
	PencilIcon,
	PhoneIcon,
	PhotoIcon,
	PlusIcon,
	Squares2X2Icon,
	TrashIcon
} from '@heroicons/react/24/outline'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { buildFlowGraph } from '../../utils'
import { CARD_TYPE, CAROUSEL_TYPE, LINK_BUTTON, PHONE_CALL_BUTTON, REPLY_BUTTON, TEXT_TYPE } from '../constants'

/**
 * Modal de confirmación para borrar
 */
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, flowName }) => {
	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div 
				className="absolute inset-0 bg-black/30"
				onClick={onClose}
			/>
			<div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
				<h3 className="text-lg font-semibold text-gray-900 mb-2">
					Delete "{flowName}"?
				</h3>
				<p className="text-sm text-gray-500 mb-6">
					This action cannot be undone. All messages and options in this flow will be permanently deleted.
				</p>
				<div className="flex gap-3 justify-end">
					<button
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
					>
						Cancel
					</button>
					<button
						onClick={onConfirm}
						className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
					>
						Delete
					</button>
				</div>
			</div>
		</div>
	)
}

/**
 * Single button/suggestion display
 */
const ButtonItem = ({ button, flowDisplayNames, onNavigateToFlow, onEdit, index }) => {
	const getIcon = () => {
		switch (button.type) {
			case REPLY_BUTTON:
				return <ArrowRightIcon className="h-3.5 w-3.5" />
			case LINK_BUTTON:
				return <LinkIcon className="h-3.5 w-3.5" />
			case PHONE_CALL_BUTTON:
				return <PhoneIcon className="h-3.5 w-3.5" />
			default:
				return <ArrowRightIcon className="h-3.5 w-3.5" />
		}
	}

	const getDestination = () => {
		if (button.type === REPLY_BUTTON && button.replyFlow) {
			return flowDisplayNames[button.replyFlow] || button.replyFlow
		}
		if (button.type === LINK_BUTTON && button.link) {
			return button.link.length > 20 ? button.link.substring(0, 20) + '...' : button.link
		}
		if (button.type === PHONE_CALL_BUTTON && button.phone) {
			return button.phone
		}
		return null
	}

	const destination = getDestination()
	const isClickable = button.type === REPLY_BUTTON && button.replyFlow

	const handleNavigate = (e) => {
		e.stopPropagation()
		if (isClickable) {
			onNavigateToFlow(button.replyFlow)
		}
	}

	return (
		<div className="group flex items-center gap-2 py-2 px-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-200 hover:shadow-sm transition-all">
			<span className="text-sm text-gray-700 flex-1">{button.text}</span>
			
			{destination && (
				<button
					onClick={handleNavigate}
					className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
						isClickable 
							? 'text-indigo-600 hover:bg-indigo-50 cursor-pointer font-medium' 
							: 'text-gray-500'
					}`}
				>
					{getIcon()}
					<span>
						{destination}
					</span>
				</button>
			)}

			<button
				onClick={(e) => {
					e.stopPropagation()
					onEdit?.(index)
				}}
				className="p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
				title="Edit option"
			>
				<PencilIcon className="h-4 w-4" />
			</button>
		</div>
	)
}

/**
 * Carousel card preview
 */
const CarouselCardPreview = ({ card, index }) => {
	return (
		<div className="flex-shrink-0 w-32 bg-white rounded-lg border border-gray-200 overflow-hidden">
			{card.image?.url ? (
				<div className="h-16 bg-gray-100">
					<img 
						src={card.image.url} 
						alt={card.title || `Card ${index + 1}`}
						className="w-full h-full object-cover"
					/>
				</div>
			) : (
				<div className="h-16 bg-gray-100 flex items-center justify-center">
					<PhotoIcon className="h-6 w-6 text-gray-300" />
				</div>
			)}
			<div className="p-2">
				<p className="text-xs font-medium text-gray-700 truncate">
					{card.title || `Card ${index + 1}`}
				</p>
				{card.description && (
					<p className="text-[10px] text-gray-400 truncate mt-0.5">
						{card.description}
					</p>
				)}
				{card.buttons?.length > 0 && (
					<p className="text-[10px] text-indigo-500 mt-1">
						{card.buttons.length} {card.buttons.length === 1 ? 'button' : 'buttons'}
					</p>
				)}
			</div>
		</div>
	)
}

/**
 * Message preview with improved carousel/card display
 */
const MessageItem = ({ element, index, onEdit, onDelete }) => {
	const renderContent = () => {
		switch (element.type) {
			case CAROUSEL_TYPE:
				return (
					<div className="mt-2">
						{element.text && (
							<p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">
								{element.text.length > 120 ? element.text.substring(0, 120) + '...' : element.text}
							</p>
						)}
						<div className="flex gap-2 overflow-x-auto pb-2 scrollbar-styled">
							{element.cards?.map((card, idx) => (
								<CarouselCardPreview key={idx} card={card} index={idx} />
							))}
						</div>
					</div>
				)
			
			case CARD_TYPE:
				return (
					<div className="mt-2 flex gap-3">
						{element.image?.url ? (
							<div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
								<img 
									src={element.image.url} 
									alt={element.title || 'Card image'}
									className="w-full h-full object-cover"
								/>
							</div>
						) : (
							<div className="w-20 h-20 flex-shrink-0 rounded-lg bg-gray-100 flex items-center justify-center">
								<PhotoIcon className="h-8 w-8 text-gray-300" />
							</div>
						)}
						<div className="flex-1 min-w-0">
							{element.title && (
								<p className="text-sm font-medium text-gray-700 truncate">{element.title}</p>
							)}
							{element.description && (
								<p className="text-xs text-gray-500 line-clamp-2 mt-1">{element.description}</p>
							)}
							{element.buttons?.length > 0 && (
								<p className="text-xs text-indigo-500 mt-1">
									{element.buttons.length} {element.buttons.length === 1 ? 'button' : 'buttons'}
								</p>
							)}
						</div>
					</div>
				)
			
			default: // TEXT_TYPE
				const text = element.text || ''
				const preview = text.length > 120 ? text.substring(0, 120) + '...' : text
				return (
					<p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{preview}</p>
				)
		}
	}

	const getTypeLabel = () => {
		switch (element.type) {
			case TEXT_TYPE: return 'Text'
			case CARD_TYPE: return 'Card'
			case CAROUSEL_TYPE: return 'Carousel'
			default: return 'Message'
		}
	}

	const getTypeIcon = () => {
		switch (element.type) {
			case CAROUSEL_TYPE:
				return <Squares2X2Icon className="h-3 w-3" />
			case CARD_TYPE:
				return <PhotoIcon className="h-3 w-3" />
			default:
				return null
		}
	}

	return (
		<div className="group bg-white border border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-sm transition-all">
			<div className="flex items-start justify-between gap-2">
				<span className="flex items-center gap-1 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
					{getTypeIcon()}
					{getTypeLabel()}
				</span>
				
				{/* Edit and Delete buttons together - always visible */}
				<div className="flex items-center gap-0.5">
					<button
						onClick={() => onEdit?.(index)}
						className="p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
						title="Edit"
					>
						<PencilIcon className="h-4 w-4" />
					</button>
					<button
						onClick={() => onDelete?.(index)}
						className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
						title="Delete"
					>
						<TrashIcon className="h-4 w-4" />
					</button>
				</div>
			</div>

			{renderContent()}
		</div>
	)
}

/**
 * Compact flow editor inline
 */
const FlowEditor = ({ 
	flowId,
	flowElements = [],
	onEditElement,
	onAddElement,
	onDeleteElement,
	onEditButton,
	onAddButton,
	allButtons = [],
	flowDisplayNames,
	onNavigateToFlow
}) => {
	return (
		<div className="space-y-4">
			{/* Messages section */}
			<div>
				<div className="flex items-center justify-between mb-2">
					<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
						Messages ({flowElements.length})
					</h4>
					{flowElements.length < 3 && (
						<button
							onClick={onAddElement}
							className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
						>
							<PlusIcon className="h-3.5 w-3.5" />
							Add
						</button>
					)}
				</div>
				
				{flowElements.length === 0 ? (
					<button
						onClick={onAddElement}
						className="w-full py-4 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm hover:border-indigo-300 hover:text-indigo-500 transition-colors"
					>
						+ Add first message
					</button>
				) : (
					<div className="space-y-2">
						{flowElements.map((element, idx) => (
							<MessageItem 
								key={idx} 
								element={element} 
								index={idx}
								onEdit={onEditElement}
								onDelete={onDeleteElement}
							/>
						))}
					</div>
				)}
			</div>

			{/* Options/Responses section */}
			{allButtons.length > 0 && (
				<div>
					<div className="flex items-center justify-between mb-2">
						<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
							Options ({allButtons.length})
						</h4>
						<button
							onClick={onAddButton}
							className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
						>
							<PlusIcon className="h-3.5 w-3.5" />
							Add
						</button>
					</div>
					<div className="space-y-1.5">
						{allButtons.map((button, idx) => (
							<ButtonItem 
								key={idx}
								button={button} 
								index={idx}
								flowDisplayNames={flowDisplayNames}
								onNavigateToFlow={onNavigateToFlow}
								onEdit={onEditButton}
							/>
						))}
					</div>
				</div>
			)}

			{/* Add button option - only show if no buttons yet but has elements */}
			{allButtons.length === 0 && flowElements.length > 0 && (
				<button
					onClick={onAddButton}
					className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-xs hover:border-indigo-300 hover:text-indigo-500 transition-colors"
				>
					+ Add option/response button
				</button>
			)}
		</div>
	)
}

/**
 * Accordion item for each flow
 */
const FlowAccordionItem = React.forwardRef(({ 
	node, 
	flowElements, 
	isExpanded, 
	onToggle, 
	isSelected,
	onSelect,
	flowDisplayNames,
	isInit,
	onNavigateToFlow,
	onEditElement,
	onAddElement,
	onDeleteElement,
	onEditButton,
	onAddButton,
	onDeleteFlow,
	onRenameFlow
}, ref) => {
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [isEditingName, setIsEditingName] = useState(false)
	const [editedName, setEditedName] = useState('')
	const nameInputRef = useRef(null)
	const { id, label, elementCount } = node

	const handleEditNameClick = (e) => {
		e.stopPropagation()
		setEditedName(label)
		setIsEditingName(true)
		setTimeout(() => nameInputRef.current?.focus(), 0)
	}

	const handleSaveName = () => {
		const trimmedName = editedName.trim()
		if (trimmedName && trimmedName !== label) {
			onRenameFlow?.(id, trimmedName)
		}
		setIsEditingName(false)
	}

	const handleNameKeyDown = (e) => {
		e.stopPropagation()
		if (e.key === 'Enter') {
			handleSaveName()
		} else if (e.key === 'Escape') {
			setIsEditingName(false)
		}
	}

	// Get all buttons/suggestions from all elements
	const allButtons = useMemo(() => {
		const buttons = []
		flowElements?.forEach((element) => {
			if (!element) return // Skip undefined/null elements
			if (element.suggestions) {
				buttons.push(...element.suggestions)
			}
			if (element.buttons) {
				buttons.push(...element.buttons)
			}
			if (element.cards) {
				element.cards.forEach((card) => {
					if (card?.buttons) {
						buttons.push(...card.buttons)
					}
				})
			}
		})
		return buttons
	}, [flowElements])

	const handleDeleteClick = (e) => {
		e.stopPropagation()
		setShowDeleteModal(true)
	}

	const handleConfirmDelete = () => {
		setShowDeleteModal(false)
		onDeleteFlow?.(id)
	}

	return (
		<>
			<DeleteConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleConfirmDelete}
				flowName={label}
			/>

			<div 
				ref={ref}
				id={`flow-${id}`}
				className={`
					border rounded-lg overflow-hidden transition-all
					${isSelected ? 'border-indigo-400 shadow-md ring-2 ring-indigo-100' : 'border-gray-200'}
				`}
			>
				{/* Header */}
				<div
					onClick={() => {
						onToggle(id)
						onSelect(id)
					}}
					className={`
						flex items-center gap-3 p-3 cursor-pointer transition-colors
						${isSelected ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'}
					`}
				>
					<button className="flex-shrink-0 text-gray-400">
						{isExpanded ? (
							<ChevronDownIcon className="h-5 w-5" />
						) : (
							<ChevronRightIcon className="h-5 w-5" />
						)}
					</button>

					<div className={`p-2 rounded-lg ${isInit ? 'bg-indigo-100' : 'bg-gray-100'}`}>
						<ChatBubbleLeftRightIcon 
							className={`h-5 w-5 ${isInit ? 'text-indigo-600' : 'text-gray-500'}`} 
						/>
					</div>

					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2">
							{isEditingName && !isInit ? (
								<input
									ref={nameInputRef}
									type="text"
									value={editedName}
									onChange={(e) => setEditedName(e.target.value)}
									onBlur={handleSaveName}
									onKeyDown={handleNameKeyDown}
									onClick={(e) => e.stopPropagation()}
									className="font-medium text-gray-800 bg-white border border-indigo-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
									style={{ width: Math.max(100, editedName.length * 8 + 20) }}
								/>
							) : (
								<h3 className={`font-medium ${isInit ? 'text-indigo-700' : 'text-gray-800'}`}>
									{label}
								</h3>
							)}
							{isInit && (
								<span className="text-[10px] bg-indigo-500 text-white px-1.5 py-0.5 rounded font-medium">
									START
								</span>
							)}
						</div>
						<p className="text-xs text-gray-500 mt-0.5">
							{elementCount} {elementCount === 1 ? 'message' : 'messages'}
							{allButtons.length > 0 && ` · ${allButtons.length} ${allButtons.length === 1 ? 'option' : 'options'}`}
						</p>
					</div>

					{/* Edit and Delete buttons - only for non-init flows */}
					{!isInit && (
						<div className="flex items-center gap-0.5">
							<button
								onClick={handleEditNameClick}
								className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
								title="Rename flow"
							>
								<PencilIcon className="h-5 w-5" />
							</button>
							<button
								onClick={handleDeleteClick}
								className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
								title="Delete flow"
							>
								<TrashIcon className="h-5 w-5" />
							</button>
						</div>
					)}
				</div>

				{/* Expanded content */}
				{isExpanded && (
					<div className="border-t border-gray-100 bg-gray-50/50 p-3">
						<FlowEditor
							flowId={id}
							flowElements={flowElements}
							allButtons={allButtons}
							flowDisplayNames={flowDisplayNames}
							onNavigateToFlow={onNavigateToFlow}
							onEditElement={(idx) => onEditElement?.(id, idx)}
							onAddElement={() => onAddElement?.(id)}
							onDeleteElement={(idx) => onDeleteElement?.(id, idx)}
							onEditButton={(idx) => onEditButton?.(id, idx)}
							onAddButton={() => onAddButton?.(id)}
						/>
					</div>
				)}
			</div>
		</>
	)
})

FlowAccordionItem.displayName = 'FlowAccordionItem'

const FlowDiagram = ({
	rbmFlows,
	rbmFlowAliases = {},
	selectedFlow,
	onSelectFlow,
	// Editing callbacks
	onEditElement,
	onAddElement,
	onDeleteElement,
	onEditButton,
	onAddButton,
	onAddFlow,
	onDeleteFlow,
	onRenameFlow,
	onSwitchView
}) => {
	const [expandedFlows, setExpandedFlows] = useState({ init: true })
	const flowRefs = useRef({})

	// Build graph from flows
	const { nodes } = useMemo(
		() => buildFlowGraph(rbmFlows, rbmFlowAliases),
		[rbmFlows, rbmFlowAliases]
	)

	// Create display names map
	const flowDisplayNames = useMemo(() => {
		const names = {}
		nodes.forEach((node) => {
			names[node.id] = node.label
		})
		return names
	}, [nodes])

	const toggleExpanded = (flowId) => {
		setExpandedFlows((prev) => ({
			...prev,
			[flowId]: !prev[flowId]
		}))
	}

	// Navigate to a flow: scroll to it, expand it, select it
	const handleNavigateToFlow = useCallback((flowId) => {
		// Expand the target flow
		setExpandedFlows((prev) => ({
			...prev,
			[flowId]: true
		}))

		// Select it
		onSelectFlow?.(flowId)

		// Scroll to it after a brief delay for the DOM to update
		setTimeout(() => {
			const element = document.getElementById(`flow-${flowId}`)
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'start' })
				// Add a highlight effect
				element.classList.add('ring-4', 'ring-indigo-300')
				setTimeout(() => {
					element.classList.remove('ring-4', 'ring-indigo-300')
				}, 1500)
			}
		}, 100)
	}, [onSelectFlow])

	if (nodes.length === 0) {
		return (
			<div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
				<div className="text-center">
					<p className="text-gray-500 text-sm">No flows to display</p>
					<p className="text-gray-400 text-xs mt-1">Add elements to see the flow</p>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{nodes.map((node) => (
				<FlowAccordionItem
					key={node.id}
					ref={(el) => (flowRefs.current[node.id] = el)}
					node={node}
					flowElements={rbmFlows[node.id]}
					isExpanded={expandedFlows[node.id] === true || (node.isInit && expandedFlows[node.id] !== false)}
					onToggle={toggleExpanded}
					isSelected={selectedFlow === node.id}
					onSelect={onSelectFlow}
					flowDisplayNames={flowDisplayNames}
					isInit={node.isInit}
					onNavigateToFlow={handleNavigateToFlow}
					onEditElement={onEditElement}
					onAddElement={onAddElement}
					onDeleteElement={onDeleteElement}
					onEditButton={onEditButton}
					onAddButton={onAddButton}
					onDeleteFlow={onDeleteFlow}
					onRenameFlow={onRenameFlow}
				/>
			))}

			{/* Add new flow button */}
			<button
				onClick={onAddFlow}
				className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 text-sm hover:border-indigo-400 hover:text-indigo-500 transition-colors flex items-center justify-center gap-2"
			>
				<PlusIcon className="h-5 w-5" />
				Add new response flow
			</button>

			{/* Switch to Classic Editor - subtle link */}
			{onSwitchView && (
				<div className="flex justify-center pt-4 pb-2">
					<button
						onClick={onSwitchView}
						className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
					>
						<Cog6ToothIcon className="h-3.5 w-3.5" />
						Switch to Classic Editor
					</button>
				</div>
			)}
		</div>
	)
}

export default FlowDiagram
